import { useCallback, useEffect, useRef } from 'react';

/**
 * Fades in `.reveal` elements as they enter the viewport. Returns a
 * *callback ref* so the observer + safety timers are wired up the
 * moment React attaches the target node — not when the parent's
 * `useEffect` first runs. This is essential on detail pages
 * (JournalDetail / ArtworkDetail / ArtistDetail / ExhibitionDetail)
 * that render a Loading placeholder while their fetch resolves and
 * only mount the ref-bearing `<article>` afterwards.
 *
 * Every `.reveal` descendant that lands in the DOM (initial render
 * or via MutationObserver for async-added children) is:
 *   1. observed by the IntersectionObserver so it eases in as it
 *      scrolls into view (threshold 0.01, rootMargin '0px 0px 5% 0px'
 *      — relaxed so any part of the element entering the viewport
 *      triggers the reveal);
 *   2. armed with a 1.5 s safety timer that force-adds `.visible` if
 *      the observer never fires. Guarantees no content stays hidden
 *      even if the IO misses an async-mounted element.
 */
export function useReveal() {
    const state = useRef({ io: null, mo: null, timers: [], bound: null, counter: 0 });

    const teardown = () => {
        const s = state.current;
        if (s.io) { s.io.disconnect(); s.io = null; }
        if (s.mo) { s.mo.disconnect(); s.mo = null; }
        s.timers.forEach(clearTimeout);
        s.timers = [];
        s.bound = null;
        s.counter = 0;
    };

    const setNode = useCallback((node) => {
        // React invokes the callback ref with `null` on unmount and
        // again with the new node when it swaps in — tear down cleanly
        // in both cases so we don't leak observers across route changes
        // or async-content flips.
        teardown();
        if (!node) return;

        const s = state.current;
        s.bound = new WeakSet();
        s.io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add('visible');
                        s.io && s.io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.05, rootMargin: '0px 0px -15% 0px' },
        );

        const bind = (n) => {
            if (s.bound.has(n)) return;
            s.bound.add(n);
            n.style.setProperty('--d', `${Math.min(s.counter * 80, 480)}ms`);
            s.counter += 1;
            s.io.observe(n);
            // Safety fallback — if the observer never fires (async
            // render race, layout timing, edge case) force-reveal only
            // elements that are already within or above the viewport at
            // the 1.5 s mark. Below-the-fold elements stay hidden and
            // continue to wait for the IntersectionObserver so they
            // fade in as the visitor scrolls to them.
            const t = setTimeout(() => {
                if (n.classList.contains('visible')) return;
                const rect = n.getBoundingClientRect();
                if (rect.top < window.innerHeight) {
                    n.classList.add('visible');
                    s.io && s.io.unobserve(n);
                }
            }, 1500);
            s.timers.push(t);
        };

        node.querySelectorAll('.reveal').forEach(bind);
        s.mo = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes.forEach((child) => {
                    if (child.nodeType !== 1) return;
                    if (child.matches?.('.reveal')) bind(child);
                    child.querySelectorAll?.('.reveal').forEach(bind);
                });
            });
        });
        s.mo.observe(node, { childList: true, subtree: true });
    }, []);

    // Belt & braces — tear down on hook unmount even if React never
    // invokes the callback ref with null (should always happen, but
    // this guarantees no dangling observers survive route changes).
    useEffect(() => teardown, []);

    return setNode;
}
